import { z } from "zod/v4";
import { withZeroTransaction } from "../../zero/utils";
import type { ExpressionBuilder, Transaction, QueryRowType, PullRow } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql, type Schema } from "../../zero/schema";
import { BodyMetric } from "../body-metric";
import { Unit } from "@corpuscomplete/core/common/unit";
import { NumericTarget } from "@corpuscomplete/core/common/numeric-target";
import { AccessGrant } from "@corpuscomplete/core/auth/grant";
import type { UserActor } from "@corpuscomplete/core/actor";
import { bodyMetricGoal, goalEndReason } from "./body-metric-goal.sql";
import { createInsertSchema } from "../../utils/sql";
import { accessDenied, typedEntries } from "@corpuscomplete/core/utils";
import { temporalPlainDateSchema } from "../../common/date-time/zod";

type MaybeZodOptional<T extends z.ZodType> = T | z.ZodOptional<T>;

type TargetSchema = z.ZodObject<{
  id: z.ZodUUID;
  target: z.ZodType<NumericTarget.Info>;
  unitKey: z.ZodType<Unit.Key>;
}>;
type TargetsSchema = z.ZodObject<{
  [K in BodyMetric.Key]?: MaybeZodOptional<TargetSchema>;
}>;
type TargetsValue<S extends TargetsSchema> =
  S extends z.ZodObject<infer T>
    ? {
        [K in keyof T]: T[K] extends z.ZodOptional<z.ZodType<infer U>>
          ? (PullRow<"bodyMetricTarget", Schema> & U) | undefined
          : T[K] extends z.ZodType<infer U>
            ? PullRow<"bodyMetricTarget", Schema> & U
            : PullRow<"bodyMetricTarget", Schema>;
      }
    : never;

export namespace BodyMetricGoal {
  export const END_REASONS = goalEndReason.enumValues;
  export type EndReason = (typeof END_REASONS)[number];

  export function viewable(eb: ExpressionBuilder<"bodyMetricGoal", Schema>, actor: UserActor) {
    return eb.or(
      eb.cmp("userId", "=", actor.userId),
      eb.exists("grantsByUser", (q) =>
        AccessGrant.matching(
          q,
          actor,
          AccessGrant.criteria("user", "body_metric_goals", ["editor", "viewer"]),
        ),
      ),
    );
  }

  async function canEdit(tx: Transaction, actor: UserActor, goalId: string): Promise<boolean> {
    const goal = await tx.run(zql.bodyMetricGoal.where("id", "=", goalId).one());
    if (!goal) {
      return false;
    }
    if (goal.userId === actor.userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", goal.userId),
        actor,
        AccessGrant.criteria("user", "body_metric_goals", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  async function canAddToUser(tx: Transaction, actor: UserActor, userId: string): Promise<boolean> {
    if (actor.userId === userId) {
      return true;
    }
    const result = await tx.run(
      AccessGrant.matching(
        zql.accessGrant.where("resourceId", "=", userId),
        actor,
        AccessGrant.criteria("user", "body_metric_goals", ["editor"]),
      ).one(),
    );
    return !!result;
  }

  export const forTypeOnDate = defineQuery(
    z.object({
      userId: z.uuid(),
      type: BodyMetric.groupTypeSchema,
      date: z.iso.date(),
    }),
    ({ ctx, args: { userId, type, date } }) => {
      const onDate = Temporal.PlainDate.from(date);
      const q = zql.bodyMetricGoal
        .where("userId", "=", userId)
        .where("type", "=", type)
        .where("dateStart", "<=", onDate)
        .where((eb) => eb.or(eb.cmp("dateEnd", "IS", null), eb.cmp("dateEnd", ">=", onDate)))
        .related("targets", (t) => t.related("metric").related("unit"))
        .one();
      if (userId === ctx.actor.userId) {
        return q;
      }
      return q.where((e) => viewable(e, ctx.actor));
    },
  );

  const build = <S extends TargetsSchema>(type: BodyMetric.GroupType, targetsSchema: S) => {
    const fromGoal = (
      goal: QueryRowType<typeof forTypeOnDate> | undefined,
    ): (PullRow<"bodyMetricGoal", Schema> & { targets: TargetsValue<S> }) | null => {
      if (!goal) {
        return null;
      }
      const targets = {} as TargetsValue<S>;
      for (const key in targetsSchema.shape) {
        const schema = targetsSchema.shape[key as keyof typeof targetsSchema.shape];
        if (!schema) {
          continue;
        }
        const target = goal.targets.find((t) => t.metric?.key === key);
        const parseResult = schema.safeParse(target);
        if (parseResult.success) {
          targets[key as keyof TargetsValue<S>] = target as TargetsValue<S>[keyof TargetsValue<S>];
        } else {
          return null;
        }
      }
      return {
        ...goal,
        targets,
      };
    };

    const createSchema = createInsertSchema(bodyMetricGoal)
      .omit({ type: true, dateEnd: true, endReason: true })
      .extend({
        dateStart: temporalPlainDateSchema,
        dateTarget: temporalPlainDateSchema.nullable(),
        targets: targetsSchema,
      });

    return {
      fromGoal,
      create: withZeroTransaction(createSchema, async ({ tx, ctx, args }) => {
        // generic mutator args inference gap
        // eslint-disable-next-line cc/no-unsafe-as-assertion
        const typedArgs = args as {
          id: string;
          userId: string;
          dateStart: Temporal.PlainDate;
          dateTarget: Temporal.PlainDate | null;
          targets: S["shape"];
        };
        const { id: goalId, userId, dateStart, dateTarget, targets } = typedArgs;

        const hasPermission = await canAddToUser(tx, ctx.actor, userId);
        if (!hasPermission) {
          accessDenied("You do not have permission to add a goal to this user");
        }

        const bodyMetrics = await tx.run(zql.bodyMetric.where("key", "IS NOT", null));
        const metricsByKey = new Map<string, (typeof bodyMetrics)[number]>();
        for (const metric of bodyMetrics) {
          if (metric.key) {
            metricsByKey.set(metric.key, metric);
          }
        }

        const existingActiveGoal = await tx.run(
          zql.bodyMetricGoal
            .where("userId", "=", userId)
            .where("type", "=", type)
            .where("dateEnd", "IS", null)
            .one(),
        );
        if (existingActiveGoal) {
          throw new Error(
            `An active ${type} goal already exists. Close the existing goal before creating a new one.`,
          );
        }

        const timeNow = Temporal.Now.instant().epochMilliseconds;

        await tx.mutate.bodyMetricGoal.insert({
          id: goalId,
          userId,
          type,
          dateStart,
          dateTarget,
          dateEnd: null,
          endReason: null,
          timeCreated: timeNow,
          timeUpdated: timeNow,
        });

        for (const [bodyMetricKey, v] of typedEntries(targets)) {
          const targetInput = v as unknown as z.infer<TargetSchema>;
          if (!targetInput) {
            continue;
          }
          const metric = metricsByKey.get(bodyMetricKey);
          if (!metric) {
            throw new Error(`Metric "${bodyMetricKey}" not found`);
          }

          await tx.mutate.bodyMetricTarget.insert({
            id: targetInput.id,
            goalId,
            metricId: metric.id,
            target: targetInput.target,
            unitKey: targetInput.unitKey,
            timeCreated: timeNow,
            timeUpdated: timeNow,
          });
        }
      }),
    };
  };

  export namespace Weight {
    export const targetsSchema = z
      .object({
        weight: z.object({
          id: z.uuid(),
          target: NumericTarget.Exact.schema({ value: z.number().positive() }),
          unitKey: Unit.keySchema("mass").extract(["lb", "kg"]),
        }),
      })
      .check((ctx) => {
        const { target, unitKey } = ctx.value.weight;
        const max = BodyMetric.MASS_MAX[unitKey];
        if (target.value > max) {
          ctx.issues.push({
            code: "custom",
            message: `Weight must be at most ${max} ${unitKey}`,
            input: ctx.value.weight,
          });
        }
      }) satisfies TargetsSchema;
    export type Targets = z.infer<typeof targetsSchema>;

    export type Create = z.infer<typeof create.schema>;
    export const { fromGoal, create } = build("weight", targetsSchema);
    export type Info = NonNullable<ReturnType<typeof fromGoal>>;
  }

  export const cancel = withZeroTransaction(
    z.object({
      goalId: z.uuid(),
    }),
    async ({ tx, ctx, args }) => {
      const { goalId } = args;

      const hasPermission = await canEdit(tx, ctx.actor, goalId);
      if (!hasPermission) {
        accessDenied("You do not have permission to close this goal");
      }

      const goal = await tx.run(zql.bodyMetricGoal.where("id", "=", goalId).one());
      if (!goal) {
        accessDenied("Goal not found");
      }
      if (goal.dateEnd !== null) {
        throw new Error("Goal is already closed");
      }

      const timeNow = Temporal.Now.instant().epochMilliseconds;

      await tx.mutate.bodyMetricGoal.update({
        id: goalId,
        dateEnd: Temporal.Now.plainDateISO(),
        endReason: "canceled",
        timeUpdated: timeNow,
      });
    },
  );
  export type Cancel = z.infer<typeof cancel.schema>;

  export type Info = NonNullable<QueryRowType<typeof forTypeOnDate>>;
}
